import type * as React from "react";

import { cx } from "../../lib/cx";
import s from "./Field.module.css";

export interface FieldProps extends React.ComponentPropsWithoutRef<"div"> {
  /** `id` del control que envuelve: es el `htmlFor` de la etiqueta. */
  htmlFor: string;
  label?: React.ReactNode;
  /** Línea de ayuda debajo del control. */
  hint?: React.ReactNode;
  /** Qué pasó y qué hacer. Reemplaza al hint y se anuncia como alerta. */
  error?: React.ReactNode;
  /** Marca visual junto a la etiqueta; el `required` real va en el control. */
  required?: boolean;
  /** Algo a la derecha de la etiqueta, en la misma línea: el contador de un `Textarea`. */
  trailing?: React.ReactNode;
  children: React.ReactNode;
}

/** `id` del mensaje (hint o error) de un campo, para el `aria-describedby` del control. */
export function idMensaje(htmlFor: string): string {
  return `${htmlFor}-mensaje`;
}

/** `id` de la etiqueta, para el `aria-labelledby` de un control que no es labelable (un listbox). */
export function idEtiqueta(htmlFor: string): string {
  return `${htmlFor}-etiqueta`;
}

/**
 * Etiqueta arriba, control, mensaje abajo. `Input`, `Select` y `Textarea` lo
 * usan por adentro; exportado para envolver cualquier otro control (un grupo
 * de chips, un editor) con la misma anatomía. El control tiene que llevar
 * `id={htmlFor}` y, si hay hint o error, `aria-describedby={idMensaje(htmlFor)}`.
 */
export function Field({ htmlFor, label, hint, error, required, trailing, className, children, ...rest }: FieldProps) {
  const mensaje = error ?? hint;
  const etiqueta = label ? (
    <label id={idEtiqueta(htmlFor)} htmlFor={htmlFor} className={s.etiqueta}>
      {label}
      {required ? (
        <span className={s.obligatorio} aria-hidden="true">
          *
        </span>
      ) : null}
    </label>
  ) : null;
  return (
    <div className={cx(s.campo, className)} data-error={error ? "" : undefined} {...rest}>
      {etiqueta && trailing ? (
        <span className={s.cabecera}>
          {etiqueta}
          {trailing}
        </span>
      ) : (
        etiqueta
      )}
      {children}
      {mensaje ? (
        <span id={idMensaje(htmlFor)} className={s.mensaje} role={error ? "alert" : undefined}>
          {mensaje}
        </span>
      ) : null}
    </div>
  );
}
